import { cva, type VariantProps } from 'class-variance-authority'
import { createElement, forwardRef } from 'react'
import { cn } from '@/shared/lib/utils'

/**
 * El `Text` de la app, en la web.
 *
 * Réplica de `apps/mobile/src/shared/ui/text.tsx`: las mismas variantes, los mismos pesos por
 * variante y los mismos tonos. Se copian sus decisiones, no su implementación — allá la familia se
 * resuelve con `fontFamily`, acá con `font-weight`, porque la web sí compone familia + peso.
 *
 * Del juego viejo de la app —`title`, `subtitle`, `money`, `amount`— no viajó ninguna: existen sólo
 * para las pantallas que todavía no se portaron. Sí viajó `heading`, porque la usa el estado vacío.
 *
 * ## ☠️ CADA PROPIEDAD SALE DE EXACTAMENTE UNA CLASE `cap-`, Y NO ES PROLIJIDAD
 *
 * Medido el 2026-09-08 con la pantalla abierta en el navegador: el nombre del proyecto salía en
 * **14 px cuando el diseño dice 22**. La causa es que las `cap-*` son clases propias y
 * `tailwind-merge` **no las conoce**: no las agrupa, no colapsa la vieja, deja pasar a las dos. Y
 * entre dos reglas de una sola clase decide el ORDEN DEL ARCHIVO, no quién la escribió último — así
 * que un `className="text-[22px]"` puesto por quien usa el componente perdía contra el
 * `cap-text-sm` que ponía la variante, porque la hoja del preview se inyecta DESPUÉS que las
 * utilidades de Tailwind.
 *
 * O sea: el `className` passthrough **no puede pisar un token**. Las dos consecuencias, y las dos
 * están aplicadas acá:
 *
 * 1. **Tamaño y color son VARIANTES, nunca clases sueltas.** `variant` emite un solo `cap-text-*` y
 *    `tone` un solo `cap-fg-*`. Por eso el color NO vive dentro de la variante —si viviera, pasar
 *    `tone` dejaría dos `cap-fg-*` compitiendo por orden de archivo— sino que cada variante declara
 *    su tono por defecto en `TONE_BY_VARIANT`.
 * 2. **Un tamaño que no está en la escala va por `style`**, que le gana a cualquier clase. Ya no
 *    queda ninguno: los 22 px del código del inmueble y del nombre del proyecto pasaron a ser el
 *    token `hero` de `tokens.js` —medidos sobre el frame HOME del Figma— y entran por su variante
 *    como cualquier otro tamaño.
 */
const capTextVariants = cva('', {
  variants: {
    variant: {
      /** Onboarding. Arsenal SC 32, centrado. La única serif de la app. */
      display: 'cap-text-display text-center',
      /** Título de pantalla. 28/32, ancho completo. */
      h1: 'cap-text-h1 w-full',
      /** Título de una pantalla apilada o de un bloque grande. 24/24. */
      h2: 'cap-text-h2',
      /** El texto sobre la foto del héroe: código del inmueble y nombre del proyecto. 22/26. */
      hero: 'cap-text-hero',
      /** Encabezado de sección («Mis documentos»). 20/22. */
      h3: 'cap-text-h3',
      /** Rótulo de sección EN MAYÚSCULA («SEGURIDAD»). 16/18, ancho completo. */
      h4: 'cap-text-h4 w-full uppercase',
      /** El título de un bloque: 16, el `heading` de la app. Lo usa el estado vacío. */
      heading: 'cap-text-base',
      body: 'cap-text-sm',
      /** Apoyo, ayudas, metadatos. */
      caption: 'cap-text-xs',
      /** Label en mayúscula sobre una sección. El interletrado es lo que la hace legible. */
      overline: 'cap-text-2xs uppercase tracking-widest'
    },
    /** Los grises del diseño por ROL. Exactamente uno se aplica siempre. */
    tone: {
      default: 'cap-fg-neutral-800',
      /** La tinta del `display`. */
      soft: 'cap-fg-neutral-700',
      subtle: 'cap-fg-neutral-500',
      muted: 'cap-fg-neutral-450',
      /** El gris de los rótulos en mayúscula. */
      label: 'cap-fg-neutral-400',
      brand: 'cap-fg-brand-700',
      accent: 'cap-fg-accent-600',
      inverse: 'cap-fg-neutral-0',
      danger: 'cap-fg-status-overdue'
    },
    weight: {
      light: 'cap-font-light',
      regular: 'cap-font-regular',
      medium: 'cap-font-medium',
      semiBold: 'cap-font-semi-bold',
      bold: 'cap-font-bold',
      display: 'cap-font-display',
      displayBold: 'cap-font-display-bold'
    },
    /**
     * Cifras de ancho fijo. **Obligatorio en todo número que cambie**: sin esto el `1` es más
     * angosto que el `8`, y al refrescarse un saldo el número entero se corre unos píxeles.
     */
    tabular: { true: 'tabular-nums', false: '' },
    /** Una sola línea con elipsis, el `numberOfLines={1}` de la app. */
    truncate: { true: 'truncate', false: '' }
  },
  defaultVariants: { variant: 'body', tabular: false, truncate: false }
})

type CapTextVariant = NonNullable<VariantProps<typeof capTextVariants>['variant']>
type CapTextWeight = NonNullable<VariantProps<typeof capTextVariants>['weight']>
type CapTextTone = NonNullable<VariantProps<typeof capTextVariants>['tone']>

/** El peso por variante, igual que `WEIGHT_BY_VARIANT` de la app. `weight` lo pisa. */
const WEIGHT_BY_VARIANT: Record<CapTextVariant, CapTextWeight> = {
  display: 'display',
  h1: 'semiBold',
  h2: 'semiBold',
  // El código del inmueble va en negrita y el nombre del proyecto en medium: los dos pasan su
  // `weight`, así que el defecto sólo tiene que existir.
  hero: 'bold',
  h3: 'semiBold',
  h4: 'bold',
  heading: 'bold',
  body: 'regular',
  caption: 'regular',
  overline: 'bold'
}

/**
 * El tono por variante — los mismos colores que la app escribe DENTRO de cada variante. Acá van en
 * un mapa aparte por lo que explica el bloque de arriba: si la variante emitiera su propio
 * `cap-fg-*`, pasar `tone` dejaría dos compitiendo y ganaría el que esté más abajo en la hoja
 * generada, no el que pidió quien usa el componente.
 */
const TONE_BY_VARIANT: Record<CapTextVariant, CapTextTone> = {
  display: 'soft',
  h1: 'brand',
  h2: 'brand',
  hero: 'inverse',
  h3: 'default',
  h4: 'label',
  heading: 'default',
  body: 'default',
  caption: 'subtle',
  overline: 'label'
}

export type CapTextProps = React.ComponentPropsWithoutRef<'span'> &
  Omit<VariantProps<typeof capTextVariants>, 'weight' | 'tone'> & {
    weight?: CapTextWeight
    tone?: CapTextTone
    /** El elemento a renderizar. `span` por defecto; `p`, `h2`… cuando la semántica lo pida. */
    as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'div'
  }

export const CapText = forwardRef<HTMLElement, CapTextProps>(function CapText(
  { as = 'span', variant, tone, weight, tabular, truncate, className, ...props },
  ref
) {
  const resolved: CapTextVariant = variant ?? 'body'

  // `createElement` y no `<Component />`: con la unión de etiquetas, JSX exige que el `ref` sea el
  // de UNA de ellas —el de un `div` no es el de un `h1`— y la única salida por JSX es un `any`.
  // Acá el elemento se elige por prop y el ref sólo viaja; `HTMLElement` es el tipo honesto.
  return createElement(as, {
    ...props,
    ref,
    className: cn(
      capTextVariants({
        variant,
        tone: tone ?? TONE_BY_VARIANT[resolved],
        weight: weight ?? WEIGHT_BY_VARIANT[resolved],
        tabular,
        truncate
      }),
      className
    )
  })
})
